/**
 * Canonical types for the LADP Project Scoping Assistant.
 *
 * `ProjectDoc` mirrors the backend pydantic model (CLAUDE.md §5) EXACTLY.
 * Field names must stay identical on both sides — this is the single source
 * of truth shared by chat, editable fields, live preview, and exports.
 */

// ---------------------------------------------------------------------------
// Canonical document model (CLAUDE.md §5)
// ---------------------------------------------------------------------------

export interface ProjectDoc {
  participantName: string; // submitter's name (mandatory)
  contactEmail: string; // submitter's contact email (mandatory)
  companyName: string; // the participant's company (used in the title)

  // --- Scoping block: the focusing layer this app adds ---
  mainGoal: string; // EXACTLY ONE. The primary problem to solve in the month.
  stretchGoals: string[]; // ORDERED by priority. MAX 3. May be empty.

  // --- The 14 template questions (preserve order & wording) ---
  problemStatement: string;
  datasetAvailable: string;
  whatTheAppDoes: string;
  problemItSolves: string;
  users: string;
  metricsImproved: string;
  evaluationApproach: string; // how to test if recommendations are correct/appropriate
  productFeatures: string;
  uiUxGuideline: string; // look & feel; examples of similar products
  integrations: string; // integration with other systems
  techniquesLibrariesFrameworks: string;
  techStack: string;
  inputOutputExamples: string; // a few example user inputs + expected outputs
  otherConsiderations: string;
}

/** A partial update applied to the draft (e.g. from a streamed `field` event). */
export type ProjectDocUpdate = Partial<ProjectDoc>;

/** The 14 free-text template fields, in template order (excludes scoping block). */
export const TEMPLATE_QUESTION_FIELDS = [
  "problemStatement",
  "datasetAvailable",
  "whatTheAppDoes",
  "problemItSolves",
  "users",
  "metricsImproved",
  "evaluationApproach",
  "productFeatures",
  "uiUxGuideline",
  "integrations",
  "techniquesLibrariesFrameworks",
  "techStack",
  "inputOutputExamples",
  "otherConsiderations",
] as const;

export type TemplateQuestionField = (typeof TEMPLATE_QUESTION_FIELDS)[number];

/** Every key on ProjectDoc (scoping block + 14 questions). */
export const PROJECT_DOC_FIELDS = [
  "companyName",
  "mainGoal",
  "stretchGoals",
  ...TEMPLATE_QUESTION_FIELDS,
] as const;

export type ProjectDocField = keyof ProjectDoc;

/** Max number of stretch goals before the UI surfaces a gentle warning (CLAUDE.md §5). */
export const MAX_STRETCH_GOALS = 3;

/** Human-readable labels + scope hints for each field, in render order. */
export interface FieldMeta {
  key: TemplateQuestionField;
  label: string;
  hint: string;
  placeholder: string;
}

export const TEMPLATE_FIELD_META: readonly FieldMeta[] = [
  {
    key: "problemStatement",
    label: "Problem Statement",
    hint: "What problem are you solving? Keep it specific and grounded.",
    placeholder: "Describe the core problem in one or two sentences…",
  },
  {
    key: "datasetAvailable",
    label: "Dataset Available",
    hint: "What data do you have today? Source, size, format, access.",
    placeholder: "e.g. 5k support tickets (CSV), exported monthly…",
  },
  {
    key: "whatTheAppDoes",
    label: "What the App Does",
    hint: "Describe the app's behaviour in plain language.",
    placeholder: "The app takes … and produces …",
  },
  {
    key: "problemItSolves",
    label: "Problem It Solves",
    hint: "Who benefits and how, concretely?",
    placeholder: "It saves the support team … by …",
  },
  {
    key: "users",
    label: "Users",
    hint: "Who will use this? Be specific about roles.",
    placeholder: "e.g. internal support agents, customers…",
  },
  {
    key: "metricsImproved",
    label: "Metrics Improved",
    hint: "How will you know it worked? Pick measurable metrics.",
    placeholder: "e.g. average handling time, deflection rate…",
  },
  {
    key: "evaluationApproach",
    label: "Evaluation Approach",
    hint: "How will you test that outputs are correct/appropriate?",
    placeholder: "e.g. labelled test set, human review rubric…",
  },
  {
    key: "productFeatures",
    label: "Product Features",
    hint: "List the features. Mark what's essential vs nice-to-have.",
    placeholder: "Essential: … · Nice-to-have: …",
  },
  {
    key: "uiUxGuideline",
    label: "UI / UX Guideline",
    hint: "Look & feel; examples of similar products.",
    placeholder: "e.g. clean chat UI like …; minimal, fast…",
  },
  {
    key: "integrations",
    label: "Integrations",
    hint: "Integration with other systems.",
    placeholder: "e.g. Slack, internal CRM API…",
  },
  {
    key: "techniquesLibrariesFrameworks",
    label: "Techniques, Libraries & Frameworks",
    hint: "Realistic approaches for your data and goal.",
    placeholder: "e.g. RAG with embeddings, LangChain…",
  },
  {
    key: "techStack",
    label: "Tech Stack",
    hint: "Languages, services, infra you'll build on.",
    placeholder: "e.g. Python, FastAPI, React, a vector DB…",
  },
  {
    key: "inputOutputExamples",
    label: "Input / Output Examples",
    hint: "A few example user inputs + expected outputs.",
    placeholder: "Input: … → Output: …",
  },
  {
    key: "otherConsiderations",
    label: "Other Considerations",
    hint: "Risks, constraints, privacy, anything else.",
    placeholder: "e.g. PII handling, latency budget…",
  },
];

/** Build an empty ProjectDoc with all string fields blank. */
export function emptyProjectDoc(): ProjectDoc {
  return {
    participantName: "",
    contactEmail: "",
    companyName: "",
    mainGoal: "",
    stretchGoals: [],
    problemStatement: "",
    datasetAvailable: "",
    whatTheAppDoes: "",
    problemItSolves: "",
    users: "",
    metricsImproved: "",
    evaluationApproach: "",
    productFeatures: "",
    uiUxGuideline: "",
    integrations: "",
    techniquesLibrariesFrameworks: "",
    techStack: "",
    inputOutputExamples: "",
    otherConsiderations: "",
  };
}

// ---------------------------------------------------------------------------
// Chat message types
// ---------------------------------------------------------------------------

export type ChatRole = "user" | "assistant";

export interface ChatMessage {
  id: string;
  role: ChatRole;
  content: string;
  /** True while the assistant message is still streaming tokens in. */
  streaming?: boolean;
  createdAt: number;
}

/** Request body sent to POST /api/chat (matches the backend ChatRequest model). */
export interface ChatRequest {
  /** Full running conversation: prior turns plus the new user message. */
  messages: { role: ChatRole; content: string }[];
  doc?: ProjectDoc;
}

// ---------------------------------------------------------------------------
// SSE event contract (CLAUDE.md §6) — keep stable.
// Wire shape (data payloads). The `event:` line carries the type.
// ---------------------------------------------------------------------------

export type SSEEventType = "token" | "field" | "done" | "error";

/** `token` — assistant prose delta. */
export interface TokenEvent {
  type: "token";
  /** The text delta to append to the streaming assistant message. */
  text: string;
}

/** `field` — `{ name, value }` doc field update. */
export interface FieldEvent {
  type: "field";
  name: ProjectDocField;
  value: string | string[];
}

/** `done` — end of turn. */
export interface DoneEvent {
  type: "done";
}

/** `error` — `{ message }`. */
export interface ErrorEvent {
  type: "error";
  message: string;
}

export type SSEEvent = TokenEvent | FieldEvent | DoneEvent | ErrorEvent;

// ---------------------------------------------------------------------------
// Draft + submission + admin API shapes
// ---------------------------------------------------------------------------

export interface DraftResponse {
  doc: ProjectDoc;
  messages?: ChatMessage[];
  updatedAt?: string;
}

export type ExportFormat = "docx" | "pdf";

export interface SubmissionResponse {
  submissionId: string;
  status: "submitted";
  recipients: string[];
}

export interface EmailListResponse {
  emails: string[]; // the backend returns a plain list of addresses
}

export interface SubmissionRecord {
  submissionId: string;
  userId: string;
  userEmail: string;
  companyName: string;
  mainGoal: string;
  stretchGoals: string[];
  submittedAt: string;
  docxPath?: string;
  pdfPath?: string;
}

export interface UserProfile {
  uid: string;
  email: string;
  admin: boolean;
}
